import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./GuidedTour.scss";

const HIGHLIGHT_PADDING = 8;
const FIND_RETRY_INTERVAL_MS = 150;
// Resumed sessions (refresh, or navigating back into this page) can take
// noticeably longer than a fresh forward navigation to fetch/hydrate data
// before a step's target renders. Give it a generous window rather than
// silently giving up.
const FIND_RETRY_LIMIT = 80;
const CALLOUT_WIDTH = 320;
// Rough estimate of the callout's rendered height, used to keep it clamped
// within the viewport (the callout is `position: fixed`, so once its inline
// `top` pushes it past the viewport bottom there is no scroll amount that
// can bring it back — unlike a normal in-flow element, scrollIntoView on it
// is a no-op).
const CALLOUT_HEIGHT_ESTIMATE = 220;
const MOBILE_BREAKPOINT = 768;
// On mobile the callout docks as a fixed "top sheet" pinned to the top of
// the viewport (see `is-top-sheet` below) instead of floating beside/below
// the target like on desktop. Centering the target there (as desktop does)
// tucks its top edge — and the sheet's own action button — behind that
// fixed overlay. This is how much vertical clearance to leave above the
// target instead, roughly the sheet's own height plus breathing room.
const MOBILE_TOP_SHEET_CLEARANCE = CALLOUT_HEIGHT_ESTIMATE + 24;
/** Applied to the actual spotlighted DOM element (not the overlay) while
 * its step is current, so consumers can style the real element directly. */
const ACTIVE_STEP_CLASS = "guided-tour-step-active";

/**
 * Generic single-step-at-a-time product tour: locates the current step's
 * target element (found via `document.querySelector(step.target)`) and
 * shows a floating callout next to it with an action button — no dimmed
 * backdrop or spotlight box; the page stays fully interactive. Advancing
 * past the last step ends it for the current mount. Pass `storageKey` to
 * additionally persist completion to sessionStorage so it won't replay on
 * remount within the same session; omit it (default) to always replay on
 * every mount, e.g. arriving at the page fresh or on a refresh.
 *
 * Step schema (all optional except `target`):
 * - requireValue / errorMessage: block advancing + show an inline error
 *   until the target's own form control (or the first one nested inside
 *   it) has a value.
 * - isComplete(rootEl): predicate re-evaluated live (on any input/change
 *   bubbling from inside the target) — e.g. "all three fields filled" or
 *   "fund allocation totals 100%".
 * - hideActionUntilComplete: don't render the action button at all until
 *   isComplete() is true (instead of showing it disabled/with an error).
 * - actionLabel: static override for the button text (default "Next", or
 *   "Got it" on the last step).
 * - onAction(finish, advance): custom handler invoked instead of the default
 *   advance-to-next-step/finish behavior. Call `advance()` to continue the
 *   tour normally (next step, or finish if this is the last one) after your
 *   action runs, or call `finish()` directly to end the tour early
 *   regardless of step position.
 * - calloutPosition: "right" or "left" to place the callout beside the
 *   target (viewport width and room permitting) instead of below/above it;
 *   ignored below MOBILE_BREAKPOINT, where it always falls back to
 *   below/above.
 *
 * The current step's real target element also gets the
 * `guided-tour-step-active` class for the duration of that step (added/
 * removed as steps change), so it can be styled directly.
 */
/** For a step's target, resolve the actual form control to read a value from
 * (the target itself if it's already an input/select/textarea, otherwise the
 * first one nested inside it — e.g. a wrapping `.form-row` div). */
/** Scrolls a step's target into view, keeping it clear of the mobile
 * top-sheet callout instead of centering it underneath. Desktop keeps the
 * original center behavior, where the callout floats beside/below and
 * doesn't cover the target. */
const scrollTargetIntoView = (el, mobileClearance = MOBILE_TOP_SHEET_CLEARANCE) => {
  if (!el || typeof window === "undefined") return;
  if (window.innerWidth < MOBILE_BREAKPOINT) {
    const rect = el.getBoundingClientRect();
    const targetTop = rect.top + window.scrollY - mobileClearance;
    window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
    return;
  }
  if (typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const getFieldControl = (target) => {
  const el = document.querySelector(target);
  if (!el) return null;
  if (/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return el;
  return el.querySelector("input, select, textarea");
};

const GuidedTour = ({ steps, storageKey, active = true, onFinish, onActiveChange }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return storageKey ? sessionStorage.getItem(storageKey) === "1" : false;
    } catch {
      return false;
    }
  });
  const retryRef = useRef({ timer: null, attempts: 0 });
  const calloutRef = useRef(null);
  // Real rendered height of the callout, used instead of the rough
  // CALLOUT_HEIGHT_ESTIMATE for "is-above"/"is-below" placement math — a
  // short callout (no error, no action button yet) positioned using the
  // taller estimate left a visible gap between it and the target.
  const [calloutHeight, setCalloutHeight] = useState(CALLOUT_HEIGHT_ESTIMATE);
  // Read from the find/scroll effect below via ref (not the state value
  // directly) so a height change alone — which happens right after that
  // effect scrolls and the callout re-measures — doesn't itself retrigger
  // the whole find-and-scroll sequence again.
  const calloutHeightRef = useRef(calloutHeight);
  calloutHeightRef.current = calloutHeight;

  // `complete` otherwise carries its value across a step transition (it's
  // only recomputed by an effect, which runs after this render commits) —
  // so a step that just finished as "complete" would leave that stale
  // `true` sitting there for the next step's very first render, briefly
  // showing its action button (for a `hideActionUntilComplete` step) before
  // the real check corrects it. Reset it in the same render the step
  // actually changes.
  const prevStepIndexRef = useRef(stepIndex);
  if (prevStepIndexRef.current !== stepIndex) {
    prevStepIndexRef.current = stepIndex;
    if (complete) setComplete(false);
  }

  const step = steps?.[stepIndex] || null;
  const isRunning = active && !dismissed && !!step;

  const finish = useCallback(() => {
    setDismissed(true);
    try {
      if (storageKey) sessionStorage.setItem(storageKey, "1");
    } catch {
      /* ignore storage errors */
    }
    onFinish?.();
  }, [storageKey, onFinish]);

  useEffect(() => {
    onActiveChange?.(isRunning);
  }, [isRunning]);

  const measure = useCallback((el) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  const recheckComplete = useCallback(() => {
    if (!step?.isComplete) {
      setComplete(true);
      return;
    }
    const root = document.querySelector(step.target);
    setComplete(root ? !!step.isComplete(root) : false);
  }, [step]);

  // Locate the current step's target (retrying briefly for async-loaded
  // content, e.g. the fund grid), scroll it into view, then measure it.
  useEffect(() => {
    if (!isRunning || !step?.target) return undefined;

    setError("");
    retryRef.current.attempts = 0;
    let cancelled = false;
    let appliedEl = null;

    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        el.classList.add(ACTIVE_STEP_CLASS);
        appliedEl = el;
        scrollTargetIntoView(el, calloutHeightRef.current + 24);
        window.setTimeout(() => {
          if (!cancelled) {
            measure(el);
            recheckComplete();
          }
        }, 350);
        return;
      }
      retryRef.current.attempts += 1;
      if (retryRef.current.attempts < FIND_RETRY_LIMIT) {
        retryRef.current.timer = window.setTimeout(
          tryFind,
          FIND_RETRY_INTERVAL_MS,
        );
      } else if (stepIndex < steps.length - 1) {
        // Target never appeared (e.g. hidden on this viewport) — skip it.
        setStepIndex((i) => i + 1);
      } else {
        // Last step's target never appeared — nothing left to show; end
        // cleanly instead of silently stalling with no visible tour.
        finish();
      }
    };

    tryFind();

    return () => {
      cancelled = true;
      if (retryRef.current.timer) window.clearTimeout(retryRef.current.timer);
      if (appliedEl) appliedEl.classList.remove(ACTIVE_STEP_CLASS);
    };
  }, [isRunning, stepIndex, step?.target]);

  // Keep the spotlight synced with the target while this step is showing.
  useEffect(() => {
    if (!isRunning || !step?.target) return undefined;
    const onSync = () => {
      const el = document.querySelector(step.target);
      if (el) measure(el);
    };
    window.addEventListener("scroll", onSync, true);
    window.addEventListener("resize", onSync);
    return () => {
      window.removeEventListener("scroll", onSync, true);
      window.removeEventListener("resize", onSync);
    };
  }, [isRunning, step?.target, measure]);

  // Live-reevaluate isComplete() on any input/change bubbling from inside
  // the target (e.g. any of the 3 plan fields, or any fund allocation input).
  // Also recheck immediately whenever isComplete's own closure changes (e.g.
  // a consumer deriving it from reactive state rather than raw DOM values) —
  // otherwise the displayed status can lag one keystroke behind, since a
  // fresh closure only takes effect on the *next* input/change event.
  useEffect(() => {
    if (!isRunning || !step?.target || !step?.isComplete) return undefined;
    const root = document.querySelector(step.target);
    if (!root) return undefined;
    recheckComplete();
    root.addEventListener("input", recheckComplete);
    root.addEventListener("change", recheckComplete);
    return () => {
      root.removeEventListener("input", recheckComplete);
      root.removeEventListener("change", recheckComplete);
    };
  }, [isRunning, step?.target, step?.isComplete, recheckComplete]);

  // Clear a shown "required" error as soon as the user fills in the field.
  useEffect(() => {
    if (!isRunning || !step?.requireValue || !error) return undefined;
    const control = getFieldControl(step.target);
    if (!control) return undefined;
    const onInput = () => {
      if (String(control.value ?? "").trim()) setError("");
    };
    control.addEventListener("input", onInput);
    control.addEventListener("change", onInput);
    return () => {
      control.removeEventListener("input", onInput);
      control.removeEventListener("change", onInput);
    };
  }, [isRunning, step?.target, step?.requireValue, error]);

  // Keep `calloutHeight` in sync with what actually rendered (content
  // length varies: no button yet vs. button visible, an error line shown or
  // not, etc.) so the "is-above"/"is-below" placement math below doesn't
  // rely on a one-size-fits-all guess.
  useLayoutEffect(() => {
    if (!isRunning) return;
    const measured = calloutRef.current?.offsetHeight;
    if (measured && Math.abs(measured - calloutHeight) > 1) {
      setCalloutHeight(measured);
    }
  });

  // Default "advance to next step, or finish on the last one" behavior —
  // exposed to onAction (as its second arg) so a step can run a custom
  // action (e.g. trigger a real submit button) and still continue the tour
  // normally afterward, instead of onAction having to end the tour itself.
  const advance = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    setRect(null);
    setStepIndex((i) => i + 1);
  }, [stepIndex, steps.length, finish]);

  const handleAction = () => {
    if (step?.requireValue) {
      const control = getFieldControl(step.target);
      const value = control ? control.value : "";
      if (!String(value ?? "").trim()) {
        setError(step.errorMessage || "This field is required. Please fill it in to continue.");
        return;
      }
    }
    setError("");

    if (typeof step?.onAction === "function") {
      step.onAction(finish, advance);
      return;
    }

    advance();
  };

  if (!isRunning || !rect) return null;

  const pad = HIGHLIGHT_PADDING;
  const spotTop = Math.max(rect.top - pad, 0);
  const spotLeft = Math.max(rect.left - pad, 0);
  const spotWidth = rect.width + pad * 2;
  const spotHeight = rect.height + pad * 2;
  const spotBottom = spotTop + spotHeight;
  const spotRight = spotLeft + spotWidth;

  const viewportW =
    typeof window !== "undefined" ? window.innerWidth : spotRight;
  const viewportH =
    typeof window !== "undefined" ? window.innerHeight : spotBottom;

  // "right"/"left" placement only applies on wider viewports with room for
  // it; mobile always keeps the below/above behavior.
  const useRightPlacement =
    step.calloutPosition === "right" &&
    viewportW >= MOBILE_BREAKPOINT &&
    spotRight + 12 + CALLOUT_WIDTH <= viewportW;
  const useLeftPlacement =
    !useRightPlacement &&
    step.calloutPosition === "left" &&
    viewportW >= MOBILE_BREAKPOINT &&
    spotLeft - 12 - CALLOUT_WIDTH >= 0;

  // Prefer the callout below the target; flip above it if there isn't room.
  const calloutBelow = spotBottom + calloutHeight <= viewportH;

  // Below MOBILE_BREAKPOINT, targets (a whole form card, or the fund grid)
  // routinely take up most/all of the viewport height, leaving no genuine
  // above/below clearance — clamping the below/above math into whatever
  // room is left just pins the callout on top of the target's own content
  // instead of avoiding it. Docking it at a fixed spot at the top of the
  // screen instead guarantees it never covers the section below it (where
  // the target field itself lives), regardless of the target's size.
  const isMobile = viewportW < MOBILE_BREAKPOINT;

  const calloutPositionClass = useRightPlacement
    ? "is-right"
    : useLeftPlacement
      ? "is-left"
      : isMobile
        ? "is-top-sheet"
        : calloutBelow
          ? "is-below"
          : "is-above";

  // Always clamp `top` to `[16, viewportH - estimatedHeight - 16]`. The
  // callout is `position: fixed`, so it's only ever actually visible while
  // its computed position sits within the current viewport — for a target
  // taller than the viewport (a long form card, or a fund grid with many
  // rows) the raw below/above math can otherwise push it beyond the bottom
  // or above the top, where no amount of page scrolling can reveal it.
  const maxCalloutTop = Math.max(viewportH - calloutHeight - 16, 16);
  const calloutTop = calloutBelow
    ? Math.min(Math.max(spotBottom + 12, 16), maxCalloutTop)
    : Math.min(Math.max(spotTop - calloutHeight - 12, 16), maxCalloutTop);

  const calloutStyle = useRightPlacement
    ? {
        top: Math.min(Math.max(spotTop, 16), maxCalloutTop),
        left: spotRight + 12,
      }
    : useLeftPlacement
      ? {
          top: Math.min(Math.max(spotTop, 16), maxCalloutTop),
          left: spotLeft - 12 - CALLOUT_WIDTH,
        }
      : isMobile
        ? { top: 16, left: 16, right: 16, width: "auto" }
        : {
            top: calloutTop,
            left: Math.min(
              Math.max(spotLeft, 16),
              Math.max(viewportW - CALLOUT_WIDTH - 16, 16),
            ),
          };

  const showAction = !step.hideActionUntilComplete || complete;
  const actionLabel =
    step.actionLabel || (stepIndex >= steps.length - 1 ? "Got it" : "Next");

  return createPortal(
    <div className="guided-tour-overlay" role="dialog" aria-live="polite">
      <div
        ref={calloutRef}
        className={`guided-tour-callout ${calloutPositionClass}`}
        style={calloutStyle}
      >
        <span className="guided-tour-step-count">
          Step {stepIndex + 1} of {steps.length}
        </span>
        {step.title && <h6 className="guided-tour-title m-0">{step.title}</h6>}
        {step.description && (
          <p className="guided-tour-description m-0">{step.description}</p>
        )}
        {error && <p className="guided-tour-error m-0">{error}</p>}
        {showAction && (
          <div className="guided-tour-actions">
            <button
              type="button"
              className="btn primary-button regular guided-tour-next"
              onClick={handleAction}
            >
              {actionLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default GuidedTour;
